import { create } from 'zustand';
import { getIsOnPremises } from '@utils-global';
import type { AuthMethod } from '../../app/auth/authMethod';

export type AuthTokens = {
  graphToken: string;
  idToken: string;
};

interface AuthState extends Partial<AuthTokens> {
  authMethod?: AuthMethod;
  setTokens: (tokens: AuthTokens, authMethod: AuthMethod) => void;
  clearTokens: () => void;
}

// Tokens live in memory only: every pane open starts logged out and the login
// flow fills the store again (MSAL keeps its own localStorage cache, which is
// what makes silent NAA fast).
export const useAuthStorage = create<AuthState>()((set) => ({
  graphToken: undefined,
  idToken: undefined,
  authMethod: undefined,
  setTokens: (tokens, authMethod) => set(() => ({ ...tokens, authMethod })),
  clearTokens: () =>
    set(() => ({ graphToken: undefined, idToken: undefined, authMethod: undefined })),
}));

export const selectIsAuthenticated = (state: AuthState): boolean =>
  getIsOnPremises() || Boolean(state.graphToken && state.idToken);

export const useIsAuthenticated = () => useAuthStorage(selectIsAuthenticated);

// This store used to be persisted under the 'auth' key, which still holds the
// tokens of each user's last session.
const LEGACY_AUTH_STORAGE_KEY = 'auth';

try {
  window.localStorage.removeItem(LEGACY_AUTH_STORAGE_KEY);
} catch {
  // localStorage can be unavailable (e.g. blocked storage); nothing to clean up then.
}
